// ============================================================================
// Shared types and renderers for QA question controls
// Used by question-set.ts (standalone) and chat-answers.ts (pairwise)
// ============================================================================

import { debounce, showSaveIndicator } from '../utils';

// ============================================================================
// Types
// ============================================================================

export interface QaChoice {
  id: string;
  choice_text: string;
  sort_order: number;
}

export interface QaQuestionItem {
  id: string;
  question_text: string;
  help_text: string | null;
  answer_type: 'single_choice' | 'multi_choice' | 'free_text' | 'numeric' | 'date' | 'scale';
  randomize_choices: boolean;
  allow_other_text: boolean;
  min_numeric: number | null;
  max_numeric: number | null;
  unit: string | null;
  max_text_length: number | null;
  order_index: number;
  choices: QaChoice[];
}

export interface AnswerEntry {
  answerId: string;
  version: {
    answer_text: string | null;
    answer_number: number | null;
    answer_date: string | null;
    selected_choice_ids: string[] | null;
    other_text: string | null;
  } | null;
  comment_text?: string | null;
}

// ============================================================================
// Escape Helpers
// ============================================================================

export function escapeBasic(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function escapeAttr(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function formatDateDisplay(dateStr: string): string {
  // Handle ISO strings like "2025-09-05T00:00:00.000Z" or plain "2025-09-05"
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return escapeBasic(dateStr);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
}

// ============================================================================
// Control Renderers
// ============================================================================

export function getChoices(q: QaQuestionItem): QaChoice[] {
  const choices = [...q.choices];
  if (q.randomize_choices) {
    for (let i = choices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [choices[i], choices[j]] = [choices[j], choices[i]];
    }
  } else {
    choices.sort((a, b) => a.sort_order - b.sort_order);
  }
  return choices;
}

export function renderControl(q: QaQuestionItem, entry: AnswerEntry | undefined): string {
  const v = entry?.version ?? null;

  switch (q.answer_type) {
    case 'single_choice':
      return renderSingleChoice(q, v);
    case 'multi_choice':
      return renderMultiChoice(q, v);
    case 'free_text':
      return renderFreeText(q, v);
    case 'numeric':
      return renderNumeric(q, v);
    case 'date':
      return renderDate(q, v);
    case 'scale':
      return renderScale(q, v);
    default:
      return '';
  }
}

export function renderSingleChoice(q: QaQuestionItem, v: AnswerEntry['version']): string {
  const choices = getChoices(q);
  const selectedId = v?.selected_choice_ids?.[0] || '';
  const isOther = v?.other_text && !selectedId;

  let html = `<div class="radio-group single-column" data-qid="${q.id}">`;
  for (const c of choices) {
    html += `
      <label class="radio-item">
        <input type="radio" name="q-${q.id}" value="${c.id}" ${selectedId === c.id ? 'checked' : ''} />
        <span class="radio-circle"></span>
        ${escapeBasic(c.choice_text)}
      </label>
    `;
  }
  if (q.allow_other_text) {
    html += `
      <label class="radio-item">
        <input type="radio" name="q-${q.id}" value="__other__" ${isOther ? 'checked' : ''} />
        <span class="radio-circle"></span>
        Other
      </label>
      <input type="text" class="input qs-other-input" id="q-other-${q.id}"
        placeholder="Please specify..."
        value="${escapeAttr(v?.other_text || '')}"
        style="display: ${isOther ? 'block' : 'none'}" />
    `;
  }
  html += '</div>';
  return html;
}

export function renderMultiChoice(q: QaQuestionItem, v: AnswerEntry['version']): string {
  const choices = getChoices(q);
  const selectedIds = new Set(v?.selected_choice_ids || []);

  let html = `<div class="checkbox-group" data-qid="${q.id}">`;
  for (const c of choices) {
    html += `
      <label class="checkbox-item">
        <input type="checkbox" name="q-${q.id}" value="${c.id}" ${selectedIds.has(c.id) ? 'checked' : ''} />
        ${escapeBasic(c.choice_text)}
      </label>
    `;
  }
  if (q.allow_other_text) {
    html += `
      <label class="checkbox-item">
        <input type="checkbox" name="q-${q.id}" value="__other__" ${v?.other_text ? 'checked' : ''} />
        Other
      </label>
      <input type="text" class="input qs-other-input" id="q-other-${q.id}"
        placeholder="Please specify..."
        value="${escapeAttr(v?.other_text || '')}"
        style="display: ${v?.other_text ? 'block' : 'none'}" />
    `;
  }
  html += '</div>';
  return html;
}

export function renderFreeText(q: QaQuestionItem, v: AnswerEntry['version']): string {
  const maxLen = q.max_text_length || 2000;
  return `
    <textarea class="input qs-textarea" id="q-text-${q.id}"
      placeholder="Type your answer..."
      maxlength="${maxLen}"
      rows="3">${escapeBasic(v?.answer_text || '')}</textarea>
  `;
}

export function renderNumeric(q: QaQuestionItem, v: AnswerEntry['version']): string {
  const unit = q.unit ? `<span class="qs-unit">${escapeBasic(q.unit)}</span>` : '';
  return `
    <div class="qs-numeric-row">
      <input type="text" inputmode="numeric" pattern="[0-9]*" class="input qs-number-input" id="q-num-${q.id}"
        value="${v?.answer_number ?? ''}"
        placeholder="${q.min_numeric !== null && q.max_numeric !== null ? `${q.min_numeric}-${q.max_numeric}` : 'Enter a number'}" />
      ${unit}
    </div>
  `;
}

export function renderDate(q: QaQuestionItem, v: AnswerEntry['version']): string {
  return `
    <input type="date" class="input qs-date-input" id="q-date-${q.id}"
      value="${v?.answer_date || ''}" />
  `;
}

export function renderScale(q: QaQuestionItem, v: AnswerEntry['version']): string {
  const min = q.min_numeric ?? 1;
  const max = q.max_numeric ?? 5;
  const selected = v?.answer_number;

  let html = `<div class="qs-scale-row" data-qid="${q.id}">`;
  for (let i = min; i <= max; i++) {
    html += `
      <button class="qs-scale-pill ${selected === i ? 'active' : ''}"
        data-value="${i}">${i}</button>
    `;
  }
  html += '</div>';
  if (q.unit) {
    html += `<div class="qs-scale-labels"><span>${q.min_numeric ?? ''}</span><span class="qs-unit">${escapeBasic(q.unit)}</span><span>${q.max_numeric ?? ''}</span></div>`;
  }
  return html;
}

// ============================================================================
// Save Button Helper
// ============================================================================

export function renderSaveButton(id: string): string {
  return `<div class="save-btn-row">
    <button class="btn btn-primary btn-sm" id="qa-save-${id}">Save</button>
  </div>`;
}

// ============================================================================
// Event Handlers
// ============================================================================

export interface AttachHandlerOptions {
  skipFreeTextAutoSave?: boolean;
  skipMultiChoiceAutoSave?: boolean;
  skipNumericAutoSave?: boolean;
  skipSingleChoiceAutoSave?: boolean;
  skipDateAutoSave?: boolean;
  skipScaleAutoSave?: boolean;
}

export function attachQuestionHandlers(
  q: QaQuestionItem,
  onSave: (q: QaQuestionItem) => void,
  options?: AttachHandlerOptions,
): void {
  const card = document.getElementById(`q-card-${q.id}`);
  if (!card) return;

  switch (q.answer_type) {
    case 'single_choice':
      if (options?.skipSingleChoiceAutoSave) {
        // Still need to toggle Other input visibility, but don't auto-save
        attachSingleChoiceHandlers(q, card, () => {});
      } else {
        attachSingleChoiceHandlers(q, card, onSave);
      }
      break;
    case 'multi_choice':
      if (!options?.skipMultiChoiceAutoSave) {
        attachMultiChoiceHandlers(q, card, onSave);
      }
      break;
    case 'free_text':
      if (!options?.skipFreeTextAutoSave) {
        attachFreeTextHandlers(q, card, onSave);
      }
      break;
    case 'numeric':
      if (!options?.skipNumericAutoSave) {
        attachNumericHandlers(q, card, onSave);
      }
      break;
    case 'date':
      if (!options?.skipDateAutoSave) {
        attachDateHandlers(q, card, onSave);
      }
      break;
    case 'scale':
      if (options?.skipScaleAutoSave) {
        // Still need pill toggling, but don't auto-save
        attachScaleHandlers(q, card, () => {});
      } else {
        attachScaleHandlers(q, card, onSave);
      }
      break;
  }
}

function attachSingleChoiceHandlers(q: QaQuestionItem, card: HTMLElement, onSave: (q: QaQuestionItem) => void): void {
  const radios = card.querySelectorAll<HTMLInputElement>(`input[name="q-${q.id}"]`);
  const otherInput = document.getElementById(`q-other-${q.id}`) as HTMLInputElement | null;

  radios.forEach(radio => {
    radio.addEventListener('change', () => {
      if (otherInput) {
        otherInput.style.display = radio.value === '__other__' && radio.checked ? 'block' : 'none';
      }
      onSave(q);
    });
  });

  otherInput?.addEventListener('input', () => onSave(q));
}

function attachMultiChoiceHandlers(q: QaQuestionItem, card: HTMLElement, onSave: (q: QaQuestionItem) => void): void {
  const checkboxes = card.querySelectorAll<HTMLInputElement>(`input[name="q-${q.id}"]`);
  const otherInput = document.getElementById(`q-other-${q.id}`) as HTMLInputElement | null;

  checkboxes.forEach(cb => {
    cb.addEventListener('change', () => {
      if (otherInput) {
        const otherCb = card.querySelector<HTMLInputElement>(`input[name="q-${q.id}"][value="__other__"]`);
        otherInput.style.display = otherCb?.checked ? 'block' : 'none';
      }
      onSave(q);
    });
  });

  otherInput?.addEventListener('input', () => onSave(q));
}

function attachFreeTextHandlers(q: QaQuestionItem, card: HTMLElement, onSave: (q: QaQuestionItem) => void): void {
  const textarea = document.getElementById(`q-text-${q.id}`);
  textarea?.addEventListener('input', () => onSave(q));
}

function attachNumericHandlers(q: QaQuestionItem, card: HTMLElement, onSave: (q: QaQuestionItem) => void): void {
  const input = document.getElementById(`q-num-${q.id}`);
  input?.addEventListener('input', () => onSave(q));
}

function attachDateHandlers(q: QaQuestionItem, card: HTMLElement, onSave: (q: QaQuestionItem) => void): void {
  const input = document.getElementById(`q-date-${q.id}`);
  input?.addEventListener('change', () => onSave(q));
}

function attachScaleHandlers(q: QaQuestionItem, card: HTMLElement, onSave: (q: QaQuestionItem) => void): void {
  const pills = card.querySelectorAll<HTMLButtonElement>('.qs-scale-pill');
  pills.forEach(pill => {
    pill.addEventListener('click', () => {
      pills.forEach(p => p.classList.remove('active'));
      pill.classList.add('active');
      onSave(q);
    });
  });
}

// ============================================================================
// Data Collection
// ============================================================================

export function collectAnswerData(q: QaQuestionItem): Record<string, any> | null {
  switch (q.answer_type) {
    case 'single_choice': {
      const card = document.getElementById(`q-card-${q.id}`);
      const selected = card?.querySelector<HTMLInputElement>(`input[name="q-${q.id}"]:checked`);
      if (!selected) return null;
      const result: Record<string, any> = {};
      if (selected.value === '__other__') {
        const otherInput = document.getElementById(`q-other-${q.id}`) as HTMLInputElement | null;
        result.selected_choice_ids = [];
        result.other_text = otherInput?.value.trim() || '';
      } else {
        result.selected_choice_ids = [selected.value];
      }
      return result;
    }

    case 'multi_choice': {
      const card = document.getElementById(`q-card-${q.id}`);
      const checked = card?.querySelectorAll<HTMLInputElement>(`input[name="q-${q.id}"]:checked`) || [];
      const ids: string[] = [];
      let hasOther = false;
      checked.forEach(cb => {
        if (cb.value === '__other__') {
          hasOther = true;
        } else {
          ids.push(cb.value);
        }
      });
      if (ids.length === 0 && !hasOther) return null;
      const result: Record<string, any> = { selected_choice_ids: ids };
      if (hasOther) {
        const otherInput = document.getElementById(`q-other-${q.id}`) as HTMLInputElement | null;
        result.other_text = otherInput?.value.trim() || '';
      }
      return result;
    }

    case 'free_text': {
      const textarea = document.getElementById(`q-text-${q.id}`) as HTMLTextAreaElement | null;
      const text = textarea?.value.trim() || '';
      if (!text) return null;
      return { answer_text: text };
    }

    case 'numeric': {
      const input = document.getElementById(`q-num-${q.id}`) as HTMLInputElement | null;
      const val = input?.value;
      if (!val || val === '') return null;
      const num = parseInt(val, 10);
      if (isNaN(num)) return null;
      return { answer_number: num };
    }

    case 'date': {
      const input = document.getElementById(`q-date-${q.id}`) as HTMLInputElement | null;
      const val = input?.value;
      if (!val) return null;
      return { answer_date: val };
    }

    case 'scale': {
      const card = document.getElementById(`q-card-${q.id}`);
      const active = card?.querySelector<HTMLButtonElement>('.qs-scale-pill.active');
      if (!active) return null;
      return { answer_number: parseInt(active.dataset.value!, 10) };
    }

    default:
      return null;
  }
}

// ============================================================================
// Comment Input (for non-free-text questions)
// ============================================================================

export function renderCommentInput(questionId: string, commentText: string | null | undefined): string {
  return `
    <div class="qs-comment-area">
      <label class="qs-comment-label">Comment (optional):</label>
      <input type="text" class="input qs-comment-input" id="q-comment-${questionId}"
        placeholder="Add a comment..." maxlength="500"
        value="${escapeAttr(commentText || '')}" />
    </div>
  `;
}

export function collectCommentText(questionId: string): string | undefined {
  const input = document.getElementById(`q-comment-${questionId}`) as HTMLInputElement | null;
  if (!input) return undefined;
  return input.value.trim();
}

// ============================================================================
// Read-only answer display (for pairwise revealed state)
// ============================================================================

export function renderAnswerReadonly(
  q: QaQuestionItem,
  answer: {
    answer_text?: string | null;
    answer_number?: number | null;
    answer_date?: string | null;
    selected_choice_ids?: string[] | null;
    other_text?: string | null;
  }
): string {
  switch (q.answer_type) {
    case 'single_choice':
    case 'multi_choice': {
      const selectedIds = new Set(answer.selected_choice_ids || []);
      const choiceLabels = q.choices
        .filter(c => selectedIds.has(c.id))
        .map(c => escapeBasic(c.choice_text));
      if (answer.other_text) {
        choiceLabels.push(escapeBasic(answer.other_text));
      }
      return choiceLabels.join(', ') || '<span class="text-muted">-</span>';
    }
    case 'free_text':
      return answer.answer_text ? escapeBasic(answer.answer_text) : '<span class="text-muted">-</span>';
    case 'numeric': {
      if (answer.answer_number === null || answer.answer_number === undefined) {
        return '<span class="text-muted">-</span>';
      }
      const unit = q.unit ? ` ${escapeBasic(q.unit)}` : '';
      return `${Math.round(answer.answer_number)}${unit}`;
    }
    case 'date':
      return answer.answer_date ? formatDateDisplay(answer.answer_date) : '<span class="text-muted">-</span>';
    case 'scale': {
      if (answer.answer_number === null || answer.answer_number === undefined) {
        return '<span class="text-muted">-</span>';
      }
      const min = q.min_numeric ?? 1;
      const max = q.max_numeric ?? 5;
      return `${Math.round(answer.answer_number)} / ${Math.round(max)}`;
    }
    default:
      return '<span class="text-muted">-</span>';
  }
}
